import { graphql, useStaticQuery } from 'gatsby';

export interface UseSiteMetadataProps {
  aggregatorQuoteRedirectPage?: 'cover' | 'review';
  autoSelectExcessOptionsFromActiveQuote?: boolean;
  coverExcessStepMode?: 'separate' | 'unified';
  declarationAfterReview?: boolean;
  dataLayerContentType?: string;
  reviewPageVariant?: 'quote' | 'aggregator';
  includeReviewExcessExplainer?: boolean;
  includeReviewTrustPilot?: boolean;
  reviewExcessExplainerDefaultOpen?: boolean;
  reviewPageVetFeesComponent?: 'default' | 'comparison';
  reviewPageVetFeesComparisonTableDefaultOpenDesktop?: boolean;
  reviewPageVetFeesComparisonTableDefaultOpenMobile?: boolean;
  useCompactReviewHero?: boolean;
  reviewPromoCodeInterceptionEnabled?: boolean;
  reviewPromoCodeInterceptMessages?: {
    code: string;
    message: string;
  }[];
  siteUrl?: string;
  companyName?: string;
  shortName?: string;
  googleVerification?: string;
  pinterestVerification?: string;
  metaTitleFallback?: string;
  twitter?: string;
  footerNavigationColumns?: {
    header?: string;
    links: {
      label: string;
      url: string;
      target?: string;
    }[];
  }[];
  footerSecondaryNavigation?: {
    url: string;
    label: string;
  }[];
  footerSocialLinks?: {
    facebook?: string;
    instagram?: string;
    linkedIn?: string;
    pinterest?: string;
    twitter?: string;
    youTube?: string;
  };
  footerShowCookieButton?: boolean;
  footerTerms?: string;
  contactNumber?: string;
  agriaContactNumber?: string;
  trustpilotUrl?: string;
  includeGoodShoppingGuideLogo?: boolean;
  isFreeJourney?: boolean;
  subSpecies?: string;
  schemaData?: string;
  excludedRegions?: string[];
  excessTypeOverride?: 'fixed' | 'variable';
  excessOptionsMode?: 'fixed' | 'variable' | 'both';
  detailedExcessExplainer?: boolean;
  includeHearFromQuestion?: boolean;
  includeMarketingPreferences?: boolean;
  includeStandAloneAddPetButton?: boolean;
  heroDescription?: string;
  includeTrustPilot?: boolean;
  petDetailsIntroText?: string;
  heroHeading?: string;
  includeDiscountTerms?: boolean;
  includeMultiPetDiscountTerms?: boolean;
  tplInProductCard?: boolean;
  useSimpleHeader?: boolean;
  includeWhichLogos?: boolean;
  usePremiumInsteadOfPolicyTotal?: boolean;
  includePaymentMethodInformationBox?: boolean;
  priceInformation?: string;
  priceInformationTitle?: string;
  includePaymentTransactionInformationBox?: boolean;
  includeTwoReviewActionButtons?: boolean;
  nextStepButtonText?: string;
  nextStepButtonPersonalization?: boolean;
  stepHeadingPersonalization?: boolean;
  useWordsForNumbers?: boolean;
  isBrochureware?: boolean;
  includePreExistingExpander?: boolean;
}

export const useSiteMetadata = (): UseSiteMetadataProps => {
  const {
    site: { siteMetadata },
  } = useStaticQuery(graphql`
    query getSiteMetadata {
      site {
        siteMetadata {
          siteUrl
          companyName
          isBrochureware
          shortName
          googleVerification
          pinterestVerification
          metaTitleFallback
          twitter
          footerNavigationColumns {
            header
            links {
              label
              url
              target
            }
          }
          footerSecondaryNavigation {
            url
            label
          }
          footerSocialLinks {
            facebook
            instagram
            linkedIn
            pinterest
            twitter
            youTube
          }
          footerShowCookieButton
          footerTerms
          contactNumber
          agriaContactNumber
          trustpilotUrl
          includeGoodShoppingGuideLogo
          isFreeJourney
          affiliateType
          subSpecies
          schemaData
          excludedRegions
          excessTypeOverride
          excessOptionsMode
          coverExcessStepMode
          dataLayerContentType
          declarationAfterReview
          detailedExcessExplainer
          includeHearFromQuestion
          includeMarketingPreferences
          includeStandAloneAddPetButton
          heroDescription
          includeTrustPilot
          petDetailsIntroText
          heroHeading
          includeDiscountTerms
          includeMultiPetDiscountTerms
          tplInProductCard
          useSimpleHeader
          includeWhichLogos
          usePremiumInsteadOfPolicyTotal
          includePaymentMethodInformationBox
          priceInformation
          priceInformationTitle
          includePaymentTransactionInformationBox
          includeTwoReviewActionButtons
          nextStepButtonText
          nextStepButtonPersonalization
          stepHeadingPersonalization
          useWordsForNumbers
          aggregatorQuoteRedirectPage
          autoSelectExcessOptionsFromActiveQuote
          includePreExistingExpander
          reviewPageVariant
          includeReviewExcessExplainer
          includeReviewTrustPilot
          reviewExcessExplainerDefaultOpen
          reviewPageVetFeesComponent
          reviewPageVetFeesComparisonTableDefaultOpenDesktop
          reviewPageVetFeesComparisonTableDefaultOpenMobile
          useCompactReviewHero
          reviewPromoCodeInterceptionEnabled
          reviewPromoCodeInterceptMessages {
            code
            message
          }
        }
      }
    }
  `);

  return siteMetadata;
};
